import { Checkbox, LoadingSpinner, Text } from "@/components/ui";
import {
  getTreeCheckboxClassName,
  getTreeIndent,
  TREE_CONTENT_CLASS,
  TREE_ICON_CLASS,
  TREE_ROW_CLASS,
  TREE_TEXT_CLASS,
} from "@/components/features/matter/file-repo/FolderTree";
import { cn } from "@/lib/utils/cn";
import type { FirmFile } from "@/lib/types";
import {
  getFirmFileDisplayName,
  sortFirmFiles,
  type FirmFileSortKey,
} from "@/lib/utils/firmFile";
import { getFileIcon } from "@/lib/utils/matterFile";
import { ChevronRight, Eye, Folder, FolderOpen, Home } from "lucide-react";
import type React from "react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";

export const FIRM_FILE_TREE_ITEM_TYPE = "FIRM_FILE_TREE_ITEM";
const ROOT_ID = "root";

const isFirmFileTreeDragPayload = (
  payload: unknown,
): payload is { file: FirmFile } =>
  typeof payload === "object" &&
  payload !== null &&
  "file" in payload &&
  Boolean((payload as { file?: FirmFile }).file);

interface FirmFolderTreeProps {
  files: FirmFile[];
  currentFolderId: string;
  onNavigateToFolder: (folderId: string) => void;
  disabledFolderIds?: Set<string>;
  rootLabel?: string;
  showRoot?: boolean;
  showFiles?: boolean;
  enableFileDrag?: boolean;
  onMoveItem?: (item: FirmFile, targetFolderId: string) => void;
  externalDropTypes?: string[];
  onExternalDrop?: (payload: unknown, targetFolderId: string) => void;
  onOpenFile?: (item: FirmFile) => void;
  selectedFileId?: string | null;
  movingItemIds?: Set<string>;
  isSearching?: boolean;
  sortKey?: FirmFileSortKey;
  showCheckboxes?: boolean;
  selectedItemIds?: Set<string>;
  onToggleSelection?: (item: FirmFile, checked: boolean) => void;
  onToggleRootSelection?: (checked: boolean) => void;
  renderContextMenu?: (item: FirmFile, trigger: ReactNode) => React.JSX.Element;
  onExpandFolder?: (folderId: string) => void;
  unloadedFolderIds?: Set<string>;
  renderFolderFooter?: (folderId: string) => ReactNode;
  renderItemSuffix?: (item: FirmFile) => ReactNode;
}

export function FirmFolderTree(props: FirmFolderTreeProps) {
  return <FirmFolderTreeInner {...props} />;
}

function FirmFolderTreeInner({
  files,
  currentFolderId,
  onNavigateToFolder,
  disabledFolderIds,
  rootLabel = "Firm Files",
  showRoot = true,
  showFiles = false,
  enableFileDrag = false,
  onMoveItem,
  externalDropTypes,
  onExternalDrop,
  onOpenFile,
  selectedFileId,
  movingItemIds,
  isSearching = false,
  sortKey,
  showCheckboxes,
  selectedItemIds,
  onToggleSelection,
  onToggleRootSelection,
  renderContextMenu,
  onExpandFolder,
  unloadedFolderIds,
  renderFolderFooter,
  renderItemSuffix,
}: FirmFolderTreeProps) {
  const lazyFolders = Boolean(onExpandFolder);
  const lastExpandedPath = useRef<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set([ROOT_ID]),
  );

  const orderedChildrenByParent = useMemo(() => {
    const map = new Map<string, FirmFile[]>();

    files.forEach((item) => {
      if (!showFiles && !item.isFolder) {
        return;
      }

      const parentId = item.idLocation || "";
      const siblings = map.get(parentId) ?? [];
      siblings.push(item);
      map.set(parentId, siblings);
    });

    map.forEach((items, parentId) => {
      const folders = items
        .filter((item) => item.isFolder)
        .sort((a, b) =>
          getFirmFileDisplayName(a).localeCompare(getFirmFileDisplayName(b)),
        );
      const regularFiles = sortKey
        ? sortFirmFiles(
            items.filter((item) => !item.isFolder),
            sortKey,
          )
        : [...items]
            .filter((item) => !item.isFolder)
            .sort((a, b) =>
              getFirmFileDisplayName(a).localeCompare(
                getFirmFileDisplayName(b),
              ),
            );

      map.set(parentId, [...folders, ...regularFiles]);
    });

    return map;
  }, [files, showFiles, sortKey]);

  useEffect(() => {
    if (!currentFolderId) {
      lastExpandedPath.current = null;
      return;
    }
    const path = [ROOT_ID, currentFolderId];
    let parentId = currentFolderId;
    while (parentId) {
      const folder = files.find(
        (item) => item.id === parentId && item.isFolder,
      );
      if (!folder?.idLocation) break;
      path.push(folder.idLocation);
      parentId = folder.idLocation;
    }
    const pathKey = JSON.stringify(path);
    // A metadata refresh must not reopen a folder the user explicitly collapsed.
    if (lazyFolders && lastExpandedPath.current === pathKey) return;
    lastExpandedPath.current = pathKey;
    setExpandedFolders((previous) => new Set([...previous, ...path]));
  }, [currentFolderId, files, lazyFolders]);

  useEffect(() => {
    if (showRoot || lazyFolders) return;

    setExpandedFolders((prev) => {
      const next = new Set(prev);
      files
        .filter((item) => item.isFolder && item.idLocation === "")
        .forEach((folder) => {
          next.add(folder.id);
        });
      return next;
    });
  }, [files, showRoot, lazyFolders]);

  const toggleFolder = (folderId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!expandedFolders.has(folderId))
      onExpandFolder?.(folderId === ROOT_ID ? "" : folderId);
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const navigateToFolder = (folderId: string) => {
    if (onExpandFolder) {
      onExpandFolder(folderId);
      setExpandedFolders(
        (previous) => new Set([...previous, folderId || ROOT_ID]),
      );
    }
    onNavigateToFolder(folderId);
  };

  const rootChildren = orderedChildrenByParent.get("") ?? [];
  const rootFolders = rootChildren.filter((item) => item.isFolder);
  const rootFiles = rootChildren.filter((item) => !item.isFolder);
  const isRootExpanded = expandedFolders.has(ROOT_ID);
  const rootFileIds = useMemo(
    () => files.filter((item) => !item.isFolder).map((item) => item.id),
    [files],
  );

  const getSelectionState = (ids: string[]): boolean | "indeterminate" => {
    if (!selectedItemIds || ids.length === 0) return false;

    const selectedCount = ids.filter((id) => selectedItemIds.has(id)).length;
    if (selectedCount === 0) return false;
    if (selectedCount === ids.length) return true;
    return "indeterminate";
  };

  const getDescendantFileIds = (folderId: string) => {
    const ids = new Set<string>();
    const stack = [folderId];

    while (stack.length > 0) {
      const currentId = stack.pop();
      if (!currentId) continue;

      const children = orderedChildrenByParent.get(currentId) ?? [];
      children.forEach((child) => {
        if (child.isFolder) {
          stack.push(child.id);
        } else {
          ids.add(child.id);
        }
      });
    }

    return Array.from(ids);
  };

  const [{ isOverRoot }, rootDropRef] = useDrop(
    () => ({
      accept: [FIRM_FILE_TREE_ITEM_TYPE, ...(externalDropTypes ?? [])],
      canDrop: () => Boolean(onMoveItem || onExternalDrop),
      drop: (item: unknown, monitor) => {
        if (monitor.didDrop()) {
          return;
        }
        if (isFirmFileTreeDragPayload(item)) {
          onMoveItem?.(item.file, "");
          return;
        }

        onExternalDrop?.(item, "");
      },
      collect: (monitor) => ({
        isOverRoot: monitor.isOver({ shallow: true }),
      }),
    }),
    [externalDropTypes, onExternalDrop, onMoveItem],
  );

  const hasItems = rootFolders.length > 0 || rootFiles.length > 0;

  return (
    <div
      ref={rootDropRef}
      className={cn(
        "overflow-x-hidden overflow-y-auto rounded-md",
        isOverRoot && "bg-primary/5 border-primary/20 border",
      )}
    >
      {showRoot && (
        <div
          className={cn(
            TREE_ROW_CLASS,
            currentFolderId === "" &&
              !selectedFileId &&
              "bg-primary/10 text-primary",
          )}
          onClick={() => {
            navigateToFolder("");
          }}
          title={rootLabel}
        >
          {showCheckboxes && (
            <Checkbox
              checked={getSelectionState(rootFileIds)}
              onClick={(event) => {
                event.stopPropagation();
              }}
              onCheckedChange={(value) => {
                const nextChecked =
                  value === "indeterminate" ? true : Boolean(value);
                onToggleRootSelection?.(nextChecked);
              }}
              className={getTreeCheckboxClassName(
                getSelectionState(rootFileIds),
              )}
              aria-label="Select all items"
            />
          )}
          <ChevronRight
            className={cn(
              "mr-1 transition-transform",
              TREE_ICON_CLASS,
              isRootExpanded && "rotate-90",
            )}
            onClick={(event) => {
              toggleFolder(ROOT_ID, event);
            }}
          />
          <div className={TREE_CONTENT_CLASS}>
            <Folder className={cn("text-amber-500", TREE_ICON_CLASS)} />
            <Text
              variant="subHeading"
              color="foreground"
              as="span"
              className={TREE_TEXT_CLASS}
            >
              {rootLabel}
            </Text>
            {!rootLabel && <Home className={cn("ml-2", TREE_ICON_CLASS)} />}
          </div>
        </div>
      )}

      {(isRootExpanded || !showRoot) && (
        <div>
          {!hasItems ? (
            <div
              className="px-2 py-2"
              style={{ paddingLeft: `${getTreeIndent(1)}px` }}
            >
              <Text variant="subHeading" as="span">
                {isSearching ? "No results found" : "This folder is empty"}
              </Text>
            </div>
          ) : (
            <>
              {rootFolders.map((folder) => (
                <FirmFolderNode
                  key={folder.id}
                  folder={folder}
                  level={1}
                  orderedChildrenByParent={orderedChildrenByParent}
                  currentFolderId={currentFolderId}
                  expandedFolders={expandedFolders}
                  disabledFolderIds={disabledFolderIds}
                  selectedFileId={selectedFileId}
                  onNavigateToFolder={navigateToFolder}
                  onToggleFolder={toggleFolder}
                  onMoveItem={onMoveItem}
                  externalDropTypes={externalDropTypes}
                  onExternalDrop={onExternalDrop}
                  onOpenFile={onOpenFile}
                  enableFileDrag={enableFileDrag}
                  movingItemIds={movingItemIds}
                  showCheckboxes={showCheckboxes}
                  selectedItemIds={selectedItemIds}
                  onToggleSelection={onToggleSelection}
                  getSelectionState={getSelectionState}
                  getDescendantFileIds={getDescendantFileIds}
                  renderContextMenu={renderContextMenu}
                  unloadedFolderIds={unloadedFolderIds}
                  renderFolderFooter={renderFolderFooter}
                  renderItemSuffix={renderItemSuffix}
                />
              ))}
              {showFiles &&
                rootFiles.map((file) => (
                  <FirmFileTreeItem
                    key={file.id}
                    item={file}
                    level={0}
                    isSelected={Boolean(selectedItemIds?.has(file.id))}
                    isPreviewed={selectedFileId === file.id}
                    enableDrag={enableFileDrag}
                    isMoveLoading={movingItemIds?.has(file.id)}
                    onOpenFile={onOpenFile}
                    showCheckboxes={showCheckboxes}
                    onToggleSelection={onToggleSelection}
                    renderContextMenu={renderContextMenu}
                    renderItemSuffix={renderItemSuffix}
                  />
                ))}
            </>
          )}
          {renderFolderFooter?.("")}
        </div>
      )}
    </div>
  );
}

function FirmFolderNode({
  folder,
  level,
  orderedChildrenByParent,
  currentFolderId,
  expandedFolders,
  disabledFolderIds,
  selectedFileId,
  onNavigateToFolder,
  onToggleFolder,
  onMoveItem,
  externalDropTypes,
  onExternalDrop,
  onOpenFile,
  enableFileDrag,
  movingItemIds,
  showCheckboxes,
  selectedItemIds,
  onToggleSelection,
  getSelectionState,
  getDescendantFileIds,
  renderContextMenu,
  unloadedFolderIds,
  renderFolderFooter,
  renderItemSuffix,
}: {
  folder: FirmFile;
  level: number;
  orderedChildrenByParent: Map<string, FirmFile[]>;
  currentFolderId: string;
  expandedFolders: Set<string>;
  disabledFolderIds?: Set<string>;
  selectedFileId?: string | null;
  onNavigateToFolder: (folderId: string) => void;
  onToggleFolder: (folderId: string, event: React.MouseEvent) => void;
  onMoveItem?: (item: FirmFile, targetFolderId: string) => void;
  externalDropTypes?: string[];
  onExternalDrop?: (payload: unknown, targetFolderId: string) => void;
  onOpenFile?: (item: FirmFile) => void;
  enableFileDrag: boolean;
  movingItemIds?: Set<string>;
  showCheckboxes?: boolean;
  selectedItemIds?: Set<string>;
  onToggleSelection?: (item: FirmFile, checked: boolean) => void;
  getSelectionState: (ids: string[]) => boolean | "indeterminate";
  getDescendantFileIds: (folderId: string) => string[];
  renderContextMenu?: (item: FirmFile, trigger: ReactNode) => React.JSX.Element;
  unloadedFolderIds?: Set<string>;
  renderFolderFooter?: (folderId: string) => ReactNode;
  renderItemSuffix?: (item: FirmFile) => ReactNode;
}) {
  const children = orderedChildrenByParent.get(folder.id) ?? [];
  const childFolders = children.filter((item) => item.isFolder);
  const childFiles = children.filter((item) => !item.isFolder);
  const hasChildren =
    children.length > 0 || Boolean(unloadedFolderIds?.has(folder.id));
  const isExpanded = expandedFolders.has(folder.id);
  const isSelected = currentFolderId === folder.id && !selectedFileId;
  const isDisabled = disabledFolderIds?.has(folder.id) ?? false;
  const isMoveLoading = movingItemIds?.has(folder.id) ?? false;
  const checkboxState = getSelectionState(getDescendantFileIds(folder.id));

  const [{ isOverFolder }, dropRef] = useDrop(
    () => ({
      accept: [FIRM_FILE_TREE_ITEM_TYPE, ...(externalDropTypes ?? [])],
      canDrop: () => Boolean(onMoveItem || onExternalDrop) && !isDisabled,
      drop: (item: unknown, monitor) => {
        if (monitor.didDrop()) {
          return;
        }
        if (isFirmFileTreeDragPayload(item)) {
          onMoveItem?.(item.file, folder.id);
          return;
        }

        onExternalDrop?.(item, folder.id);
      },
      collect: (monitor) => ({
        isOverFolder: monitor.isOver({ shallow: true }),
      }),
    }),
    [externalDropTypes, folder.id, isDisabled, onExternalDrop, onMoveItem],
  );

  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: FIRM_FILE_TREE_ITEM_TYPE,
      item: { file: folder },
      canDrag: enableFileDrag && !isDisabled && !isMoveLoading,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [enableFileDrag, folder, isDisabled, isMoveLoading],
  );

  const trigger = (
    <div>
      <div
        ref={(node) => {
          dropRef(node);
          dragRef(node);
        }}
        className={cn(
          TREE_ROW_CLASS,
          isSelected && "bg-primary/10 text-primary",
          isDisabled && "cursor-not-allowed opacity-50",
          isOverFolder && "bg-primary/5 border-primary/20 border",
          isDragging && "opacity-50",
          isMoveLoading && "cursor-wait",
        )}
        style={{ paddingLeft: `${getTreeIndent(level)}px` }}
        onClick={() => {
          if (!isDisabled) {
            onNavigateToFolder(folder.id);
          }
        }}
        title={folder.name}
      >
        {showCheckboxes && (
          <Checkbox
            checked={checkboxState}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onCheckedChange={(value) => {
              const nextChecked =
                value === "indeterminate" ? true : Boolean(value);
              onToggleSelection?.(folder, nextChecked);
            }}
            className={getTreeCheckboxClassName(checkboxState)}
            aria-label={`Select ${folder.name}`}
          />
        )}
        <ChevronRight
          className={cn(
            "mr-1 transition-transform",
            TREE_ICON_CLASS,
            isExpanded && "rotate-90",
            !hasChildren && "opacity-0",
          )}
          onClick={(event) => {
            if (hasChildren) {
              onToggleFolder(folder.id, event);
            } else {
              event.stopPropagation();
            }
          }}
        />
        <div className={TREE_CONTENT_CLASS}>
          {isMoveLoading ? (
            <LoadingSpinner className={cn("text-blue-500", TREE_ICON_CLASS)} />
          ) : isExpanded ? (
            <FolderOpen className={cn("text-amber-500", TREE_ICON_CLASS)} />
          ) : (
            <Folder className={cn("text-amber-500", TREE_ICON_CLASS)} />
          )}
          <Text
            variant="subHeading"
            color="foreground"
            as="span"
            className={TREE_TEXT_CLASS}
          >
            {folder.name}
          </Text>
          {renderItemSuffix?.(folder)}
        </div>
      </div>

      {isExpanded && (
        <div>
          {children.length === 0 ? null : (
            <>
              {childFolders.map((childFolder) => (
                <FirmFolderNode
                  key={childFolder.id}
                  folder={childFolder}
                  level={level + 1}
                  orderedChildrenByParent={orderedChildrenByParent}
                  currentFolderId={currentFolderId}
                  expandedFolders={expandedFolders}
                  disabledFolderIds={disabledFolderIds}
                  selectedFileId={selectedFileId}
                  onNavigateToFolder={onNavigateToFolder}
                  onToggleFolder={onToggleFolder}
                  onMoveItem={onMoveItem}
                  externalDropTypes={externalDropTypes}
                  onExternalDrop={onExternalDrop}
                  onOpenFile={onOpenFile}
                  enableFileDrag={enableFileDrag}
                  movingItemIds={movingItemIds}
                  showCheckboxes={showCheckboxes}
                  selectedItemIds={selectedItemIds}
                  onToggleSelection={onToggleSelection}
                  getSelectionState={getSelectionState}
                  getDescendantFileIds={getDescendantFileIds}
                  renderContextMenu={renderContextMenu}
                  unloadedFolderIds={unloadedFolderIds}
                  renderFolderFooter={renderFolderFooter}
                  renderItemSuffix={renderItemSuffix}
                />
              ))}
              {childFiles.map((file) => (
                <FirmFileTreeItem
                  key={file.id}
                  item={file}
                  level={level}
                  isSelected={Boolean(selectedItemIds?.has(file.id))}
                  isPreviewed={selectedFileId === file.id}
                  enableDrag={enableFileDrag}
                  isMoveLoading={movingItemIds?.has(file.id)}
                  onOpenFile={onOpenFile}
                  showCheckboxes={showCheckboxes}
                  onToggleSelection={onToggleSelection}
                  renderContextMenu={renderContextMenu}
                  renderItemSuffix={renderItemSuffix}
                />
              ))}
            </>
          )}
          {renderFolderFooter?.(folder.id)}
        </div>
      )}
    </div>
  );

  return renderContextMenu ? renderContextMenu(folder, trigger) : trigger;
}

function FirmFileTreeItem({
  item,
  level,
  isSelected,
  isPreviewed,
  isMoveLoading,
  enableDrag,
  onOpenFile,
  showCheckboxes,
  onToggleSelection,
  renderContextMenu,
  renderItemSuffix,
}: {
  item: FirmFile;
  level: number;
  isSelected: boolean;
  isPreviewed?: boolean;
  isMoveLoading?: boolean;
  enableDrag: boolean;
  onOpenFile?: (item: FirmFile) => void;
  showCheckboxes?: boolean;
  onToggleSelection?: (item: FirmFile, checked: boolean) => void;
  renderContextMenu?: (item: FirmFile, trigger: ReactNode) => React.JSX.Element;
  renderItemSuffix?: (item: FirmFile) => ReactNode;
}) {
  const FileIcon = getFileIcon(getFirmFileDisplayName(item));
  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: FIRM_FILE_TREE_ITEM_TYPE,
      item: { file: item },
      canDrag: enableDrag && !isMoveLoading,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [enableDrag, item, isMoveLoading],
  );

  const trigger = (
    <div
      ref={dragRef}
      className={cn(
        TREE_ROW_CLASS,
        "relative justify-between",
        isSelected && "bg-primary/10 text-primary hover:bg-primary/20",
        isDragging && "opacity-50",
        isMoveLoading && "cursor-wait",
      )}
      style={{ paddingLeft: `${getTreeIndent(level + 1)}px` }}
      onClick={() => {
        onOpenFile?.(item);
      }}
      title={getFirmFileDisplayName(item)}
    >
      {showCheckboxes && (
        <Checkbox
          checked={isSelected}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onCheckedChange={(value) => {
            const nextChecked =
              value === "indeterminate" ? true : Boolean(value);
            onToggleSelection?.(item, nextChecked);
          }}
          className={getTreeCheckboxClassName(isSelected)}
          aria-label={`Select ${getFirmFileDisplayName(item)}`}
        />
      )}
      <div className={TREE_CONTENT_CLASS}>
        {isMoveLoading ? (
          <LoadingSpinner className={cn(TREE_ICON_CLASS, "text-blue-500")} />
        ) : (
          <FileIcon className={cn(TREE_ICON_CLASS, "text-blue-500")} />
        )}
        <Text
          variant="subHeading"
          color="foreground"
          as="span"
          className={TREE_TEXT_CLASS}
        >
          {getFirmFileDisplayName(item)}
        </Text>
        {renderItemSuffix?.(item)}
      </div>
      {isPreviewed && <Eye className={cn(TREE_ICON_CLASS, "text-blue-500")} />}
    </div>
  );

  return renderContextMenu ? renderContextMenu(item, trigger) : trigger;
}
